import React from "react";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

export function StatCard({ label, value, yoy, icon: Icon, tone = "primary", suffix, testId }) {
  const toneMap = {
    primary: "text-sams-primary bg-blue-50",
    completed: "text-emerald-600 bg-emerald-50",
    in_progress: "text-amber-600 bg-amber-50",
    overdue: "text-red-600 bg-red-50",
    planned: "text-purple-600 bg-purple-50",
    neutral: "text-slate-700 bg-slate-100",
  };
  const iconCls = toneMap[tone] || toneMap.primary;
  const trend = yoy > 0 ? "up" : yoy < 0 ? "down" : "flat";
  const trendCls = trend === "up" ? "text-emerald-600" : trend === "down" ? "text-red-600" : "text-slate-500";
  const TrendIcon = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : Minus;

  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-md p-4 hover:-translate-y-[2px] hover:shadow-sm transition-transform">
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
        {Icon && (
          <div className={`w-8 h-8 rounded-md ${iconCls} grid place-items-center`}>
            <Icon size={16} strokeWidth={1.75} />
          </div>
        )}
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-heading text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
          {value}{suffix}
        </div>
      </div>
      {typeof yoy === "number" && (
        <div className={`mt-2 flex items-center gap-1 text-xs ${trendCls}`}>
          <TrendIcon size={12} />
          <span className="font-semibold">{yoy > 0 ? "+" : ""}{yoy}%</span>
          <span className="text-slate-400">YoY</span>
        </div>
      )}
    </div>
  );
}

export function GradeBadge({ grade }) {
  if (!grade || grade === "-") return <span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-bold bg-slate-100 text-slate-500">—</span>;
  return <span className={`inline-flex px-2.5 py-0.5 rounded-sm text-xs font-bold grade-${grade}`}>{grade}</span>;
}

export function StatusBadge({ status }) {
  const label = (status || "planned").replace(/_/g, " ");
  return <span className={`inline-flex px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider status-${status || "planned"}`}>{label}</span>;
}

export function SectionCard({ title, subtitle, children, actions, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-md ${className}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export function EmptyState({ title = "No data yet", message }) {
  return (
    <div className="border border-dashed border-slate-200 rounded-md p-8 text-center bg-slate-50/40">
      <div className="font-heading font-semibold text-slate-800">{title}</div>
      {message && <div className="text-sm text-slate-500 mt-1">{message}</div>}
    </div>
  );
}
